/**
 * Utility function to build itinerary post objects for create/edit/remix operations.
 * Centralizes post object construction logic to eliminate code duplication between
 * Desktop and Mobile views.
 */

import type { CityFormData, PrimaryDestination } from '@/types/city';
import { buildItineraryCityMetadata } from './cityMetadataBuilder';
import { normalizeCost } from './costNormalizer';
import { getPhotoUrl } from '@/utils/photoUtils';
import { formatDateToYYYYMMDD } from './dateUtils';
import { isMongoObjectIdString, mongoObjectIdStringOrNull } from './idGeneration';
import { appendLanguageCodeForGooglePlacesUpsert } from '@/utils/placesLanguageCode';
import { isTravelSlotType } from '@/utils/customPlaceUpgrade';
// SourceMetadata type (previously from InspoToTripContext, now defined locally)
export interface SourceMetadata {
  source: 'inspo-to-trip' | 'create-itinerary' | 'itinerary-import';
  postId?: string;
  shortCode?: string;
  [key: string]: unknown;
}
import type { ItinerarySlot, PostLocationPayload, RawLocationData } from './itinerary/types';
import { ItineraryMode, type ModeMetadata, getTargetPostId } from './itinerary/modes';
import { warn } from '@/utils/logger';
import { withKnownWebsite } from '@/utils/websiteUtils';
import Post from '@/models/post';

/**
 * Legacy parameters (for backwards compatibility)
 */
export interface LegacyPostBuilderParams {
  isEdit: boolean;
  editingPostId: string | null;
  urlPostId: string | null;
  remixPostObj: Record<string, unknown> | null;
  formData: Record<string, unknown>;
  tripTitle: string;
  actualDayCount: number;
  city: CityFormData | null | undefined;
  itineraryData: ItinerarySlot[];
  timelineData?: Array<{ travelDay?: string; [key: string]: unknown }>; // Day notes from timeline
  itineraryNotes?: string; // Post-level notes for the entire itinerary
  /** True only after the user edits the overview field. An empty editor must not wipe stored notes. */
  itineraryNotesCleared?: boolean;
  /** True after the user edits a day note. An empty editor must not wipe stored day notes. */
  dayNotesEdited?: boolean;
  sourceMetadata?: SourceMetadata | null;
  sourceSocialMediaPostId?: string | null;
  isDraft?: boolean;
  remixDraftId?: string | null;
  existingPost?: Post | null; // Optional existing post to preserve photos
  isPrivate?: boolean; // Post visibility setting
  keepDateStringsWithDayCount?: boolean;
}

/**
 * New mode-based parameters
 */
export interface ModeBasedPostBuilderParams {
  mode: ItineraryMode;
  modeMetadata: ModeMetadata;
  formData: Record<string, unknown>;
  tripTitle: string;
  actualDayCount: number;
  city: CityFormData | null | undefined;
  itineraryData: ItinerarySlot[];
  timelineData?: Array<{ travelDay?: string; [key: string]: unknown }>; // Day notes from timeline
  itineraryNotes?: string; // Post-level notes for the entire itinerary
  /** True only after the user edits the overview field. An empty editor must not wipe stored notes. */
  itineraryNotesCleared?: boolean;
  /** True after the user edits a day note. An empty editor must not wipe stored day notes. */
  dayNotesEdited?: boolean;
  sourceMetadata?: SourceMetadata | null;
  sourceSocialMediaPostId?: string | null;
  isDraft?: boolean;
  existingPost?: Post | null; // Optional existing post to preserve photos
  isPrivate?: boolean; // Post visibility setting
  /** When true, NEW mode sends start/end date strings even if is_using_day_count is true (itinerary import). */
  keepDateStringsWithDayCount?: boolean;
}

/**
 * Union type for backwards compatibility
 */
export type PostBuilderParams = LegacyPostBuilderParams | ModeBasedPostBuilderParams;

export interface ItineraryPostObject {
  _id?: string;
  name?: string;
  body?: string;
  post_type?: string;
  post: {
    _id?: string;
    name: string;
    body: string;
    post_type?: string;
    city_identifying_names: string[];
    is_draft: boolean;
    is_private: boolean;
    is_hidden: boolean;
    tag_list?: string[];
    itinerary_data?: {
      day_count: number;
      traveler_count: number;
      is_using_day_count: boolean;
      user_defined_destinations: Array<{
        name: string;
        location_id: string;
        google_place_id?: string;
        lat: number;
        lng: number;
      }>;
      start_date_string?: string;
      end_date_string?: string;
    };
    collaborators_emails?: string[];
    vibe_description?: string;
    budget?: string;
    source_metadata?: SourceMetadata;
    source_social_media_post_id?: string;
    primary_destination?: PrimaryDestination | null;
    [key: string]: unknown; // Allow spreading remixPostObj
  };
  itinerary_slots: ItinerarySlot[];
  lodging_periods: unknown[];
  postLocations?: PostLocationPayload[];
  post_locations?: PostLocationPayload[];
}

/**
 * Builds an itinerary post object for create, edit, or remix operations.
 * 
 * @param params - Parameters for building the post object
 * @returns Complete itinerary object ready for API submission
 */
function deepClone<T>(value: T): T {
  return value ? JSON.parse(JSON.stringify(value)) : value;
}

export function normalizeGooglePlacesData(raw: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const data = raw ? deepClone(raw) : {};

  const resolvedAddressComponents =
    Array.isArray(data.addressComponents)
      ? data.addressComponents
      : Array.isArray(data.address_components)
      ? data.address_components
      : [];

  const normalizedAddressComponents = resolvedAddressComponents
    .map((component: Record<string, unknown>) => {
      if (!component) {
        return null;
      }

      const clone = deepClone(component);

      const types =
        Array.isArray(clone.types) && clone.types.length > 0
          ? clone.types
          : clone.type
          ? [clone.type]
          : ['establishment'];

      clone.types = types;
      clone.longText =
        clone.longText ??
        clone.long_name ??
        clone.longtext ??
        clone.long ??
        clone.text ??
        '';
      clone.shortText =
        clone.shortText ??
        clone.short_name ??
        clone.shorttext ??
        clone.short ??
        clone.label ??
        clone.longText ??
        '';

      return clone;
    })
    .filter(Boolean);

  data.addressComponents = normalizedAddressComponents;
  data.address_components = normalizedAddressComponents
    .filter((component): component is Record<string, unknown> => component !== null)
    .map((component: Record<string, unknown>) => {
    if (!component) {
      return component;
    }
    const clone = deepClone(component);
    clone.long_name = clone.longText ?? clone.long_name ?? '';
    clone.short_name = clone.shortText ?? clone.short_name ?? '';
    return clone;
  });

  if (!Array.isArray(data.types) || data.types.length === 0) {
    const derivedTypes = Array.isArray(data.addressComponents) && data.addressComponents[0]
      ? data.addressComponents[0].types
      : undefined;

    const topLevelType =
      typeof data.type === 'string'
        ? [data.type]
        : Array.isArray(derivedTypes) && derivedTypes.length > 0
        ? derivedTypes
        : ['establishment'];

    data.types = topLevelType;
  }

  // Social scrapers / legacy payloads often use place_id + formatted_address; readers expect id + formattedAddress.
  const idStr = typeof data.id === 'string' ? data.id.trim() : '';
  if (!idStr) {
    const fromLegacy =
      (typeof data.place_id === 'string' && data.place_id.trim()) ||
      (typeof data.placeId === 'string' && data.placeId.trim()) ||
      '';
    if (fromLegacy) {
      data.id = fromLegacy;
    }
  }
  if (typeof data.formattedAddress !== 'string' && typeof data.formatted_address === 'string') {
    data.formattedAddress = data.formatted_address;
  }

  return data;
}

/**
 * Maps photo objects to URL strings.
 * Only supports new identifier format (name/photo_reference) - no legacy thumbUri/fullUri.
 * Uses photoUtils to convert identifiers to proxy URLs via the photos endpoint.
 */
export function mapPhotosToUrls(photos: Array<{ name?: string; photo_reference?: string; url?: string }> | undefined): string[] {
  if (!Array.isArray(photos)) {
    return [];
  }
  
  return photos
    .map((photo) => {
      // Handle new photo identifier format (backend sends identifiers only)
      if (photo?.name || photo?.photo_reference) {
        const photoUrl = getPhotoUrl(photo as { name?: string; photo_reference?: string }, 400);
        if (photoUrl) {
          return photoUrl;
        }
      }
      
      // Handle user-uploaded photos (S3 URLs in url field)
      if (photo?.url && typeof photo.url === 'string' && photo.url.startsWith('http')) {
        return photo.url;
      }
      
      return '';
    })
    .filter((url): url is string => typeof url === 'string' && url.length > 0);
}

/**
 * Google Places data structure (partial, for place_id extraction)
 */
interface GooglePlacesDataForPlaceId {
  place_id?: string;
  id?: string;
  [key: string]: unknown;
}

/**
 * Extracts google_place_id from various possible sources
 */
function extractGooglePlaceId(
  slot: ItinerarySlot | undefined,
  locationDataTyped: RawLocationData,
  googlePlacesData: GooglePlacesDataForPlaceId | null | undefined
): string {
  return (
    slot?.placeId ||
    locationDataTyped.google_place_id ||
    googlePlacesData?.place_id ||
    googlePlacesData?.id ||
    ''
  );
}

function toPostLocation(
  slot: ItinerarySlot,
  editingPostId: string | null
): PostLocationPayload | null {
  let locationData = slot?.locationData || slot?.location;
  const isUserCreatedEarly =
    slot?.isUserCreated === true ||
    (locationData as RawLocationData | undefined)?.is_user_created === true;
  if (!locationData && isUserCreatedEarly && typeof slot?.name === 'string' && slot.name.trim()) {
    locationData = { name: slot.name.trim(), is_user_created: true };
  }
  if (!locationData) {
    return null;
  }

  const locationDataTyped = locationData as RawLocationData;
  // Travel is a custom stop. Send both slotType and gt_category so createItinerary
  // does not prefer a leftover category, and do not attach a Google location.
  const rawSlotType = slot?.slotType || locationDataTyped.slotType || null;
  const travelRow = isTravelSlotType(typeof rawSlotType === 'string' ? rawSlotType : null);
  const slotType = travelRow ? 'travel' : rawSlotType;
  const gtCategory = travelRow ? 'travel' : slot?.gt_category || undefined;
  // location_id should be the Location _id (MongoDB ObjectId), NOT the google_place_id
  // If we don't have a Location _id, set location_id to empty string so backend can create/find Location
  // using the location object (google_place_id, name, geoposition, google_places_data)
  const getLocationId = (): string => {
    // Only use actual Location _id values (MongoDB ObjectIds), not google_place_id
    // Check for _id or location_id (these are Location document IDs)
    const locationId = locationDataTyped._id || locationDataTyped.location_id;
    if (locationId && typeof locationId === 'string') {
      if (isMongoObjectIdString(locationId)) {
        return locationId;
      }
    }
    return "";
  };
  const locationId: string = travelRow ? '' : getLocationId();

  // Only preserve _id if updating an existing post location
  // For new post locations, omit _id and let backend generate unique MongoDB ObjectId
  // Backend handles duplicate location references by generating unique IDs for each post_location
  const baseId: string | undefined = (() => {
    const slotId = slot?._id;
    if (slotId && typeof slotId === 'string') return slotId;
    // Don't generate ID - backend will generate it
    return undefined;
  })();

  const createdAt =
    locationDataTyped.created_at ||
    locationDataTyped.createdAt ||
    new Date().toISOString();

  const updatedAt = new Date().toISOString();

  // Ensure google_places_data has photos and formattedAddress for persistence
  // Handle nested location structure (locationDataTyped.location.google_places_data)
  const nestedLocation = (locationDataTyped as Record<string, unknown>).location;
  const nestedGooglePlacesData: Record<string, unknown> | null | undefined = 
    nestedLocation && typeof nestedLocation === 'object'
      ? ((nestedLocation as Record<string, unknown>).google_places_data ||
         (nestedLocation as Record<string, unknown>).googlePlacesData) as Record<string, unknown> | undefined
      : null;

  const existingGooglePlacesData = normalizeGooglePlacesData(
    (locationDataTyped.google_places_data ||
      locationDataTyped.googlePlacesData ||
      nestedGooglePlacesData) as Record<string, unknown> | null | undefined
  );
  
  // Populate photos in google_places_data if missing but available in locationData
  const existingPhotos = existingGooglePlacesData.photos;
  const hasExistingPhotos = Array.isArray(existingPhotos) && existingPhotos.length > 0;
  if (!hasExistingPhotos) {
    const photosFromLocationData = Array.isArray(locationDataTyped.photos) ? locationDataTyped.photos : [];
    const imageUrlsFromSlot = Array.isArray(slot?.image_urls) ? slot.image_urls : [];
    
    if (photosFromLocationData.length > 0) {
      // Use photos from locationData if available
      existingGooglePlacesData.photos = photosFromLocationData;
    } else if (imageUrlsFromSlot.length > 0) {
      // Convert image_urls to photo objects for google_places_data (user-uploaded S3 URLs)
      existingGooglePlacesData.photos = imageUrlsFromSlot.map((url: string) => ({
        url: url,
      }));
    }
  }
  
  const notes: string =
    typeof slot?.notes === 'string' ? slot.notes : '';

  const dayNotes =
    slot?.dayNotes ||
    slot?.travelDay ||
    locationDataTyped.dayNotes ||
    locationDataTyped.travelDay ||
    locationDataTyped.day_notes ||
    locationDataTyped.day_label ||
    null;

  // Determine if this is a user-created place
  // Priority: slot.isUserCreated > locationDataTyped.is_user_created > locationDataTyped.isUserCreated
  const isUserCreated = travelRow || (
    slot?.isUserCreated ??
    locationDataTyped.is_user_created ??
    locationDataTyped.isUserCreated ??
    false
  );

  // For user-created places: body comes from slot.name (user-entered title)
  // For Google Places: body should be location name (slot.name or locationDataTyped.name), not notes
  // ItinerarySlot for user-created places has 'name' field, which becomes PostLocation.body
  const getBodyValue = (): string => {
    if (isUserCreated) {
      // For user-created places, use slot.name (which contains the user-entered title)
      // This becomes PostLocation.body in the backend
      const slotName = typeof slot?.name === 'string' ? slot.name.trim() : '';
      return slotName || '';
    }
    // For Google Places: prefer slot.name (location name), then locationDataTyped.name, then notes
    // This ensures the location name is saved as body, not empty notes
    const slotName = typeof slot?.name === 'string' ? slot.name.trim() : '';
    const locationName = typeof locationDataTyped?.name === 'string' ? locationDataTyped.name.trim() : '';
    return slotName || locationName || '';
  };
  const bodyValue: string = getBodyValue();

  // For user-created places: address comes from slot (user-entered address)
  // For Google Places: address can come from slot or locationDataTyped
  const getAddressValue = (): string => {
    if (isUserCreated) {
      return typeof slot?.address === 'string' ? slot.address.trim() : '';
    }
    const slotAddress = typeof slot?.address === 'string' ? slot.address.trim() : '';
    const locationAddress = typeof locationDataTyped.address === 'string' ? locationDataTyped.address.trim() : '';
    return slotAddress || locationAddress || '';
  };
  const addressValue: string = getAddressValue();

  // Populate formattedAddress in google_places_data if missing but address is available
  if (!existingGooglePlacesData.formattedAddress && addressValue) {
    existingGooglePlacesData.formattedAddress = addressValue;
  }
  
  // Preserve the original google_places_data structure (snake_case) from locationDataTyped
  // Backend expects snake_case fields (place_id, formatted_address, geometry)
  const originalGooglePlacesData = locationDataTyped.google_places_data || locationDataTyped.googlePlacesData;
  
  // If we have original google_places_data with snake_case, preserve it
  // Otherwise use the normalized version (which may have converted to camelCase)
  const originalAsRecord = originalGooglePlacesData as Record<string, unknown> | null | undefined;
  const hasSnakeCaseFields = originalGooglePlacesData && originalAsRecord &&
    (typeof originalAsRecord.place_id !== 'undefined' || 
     typeof originalAsRecord.formatted_address !== 'undefined' || 
     typeof originalAsRecord.geometry !== 'undefined');
  const finalGooglePlacesData = hasSnakeCaseFields
    ? originalGooglePlacesData // Use original if it has snake_case fields
    : existingGooglePlacesData; // Otherwise use normalized version
  
  const camelCaseLocation = {
    ...locationDataTyped,
    name: locationDataTyped.name || (isUserCreated ? '' : bodyValue),
    google_place_id: extractGooglePlaceId(slot, locationDataTyped, finalGooglePlacesData as GooglePlacesDataForPlaceId | null | undefined),
    a3_intl_address: locationDataTyped.a3_intl_address || addressValue || 
      (finalGooglePlacesData && typeof finalGooglePlacesData === 'object' 
        ? (finalGooglePlacesData as Record<string, unknown>).formatted_address ||
          (finalGooglePlacesData as Record<string, unknown>).formattedAddress
        : null) || '',
    website: locationDataTyped.website || undefined, // Explicitly include website in location object
    google_places_data: finalGooglePlacesData,
    geoposition: locationDataTyped.geoposition || (slot?.location && typeof slot.location === 'object' && 'lat' in slot.location && 'lng' in slot.location
      ? {
          type: 'Point' as const,
          coordinates: [slot.location.lng as number, slot.location.lat as number] as [number, number],
        }
      : undefined),
  };

  if (travelRow) {
    camelCaseLocation.google_place_id = '';
    delete (camelCaseLocation as { _id?: unknown })._id;
    delete (camelCaseLocation as { location_id?: unknown }).location_id;
  } else if (!isUserCreated) {
    appendLanguageCodeForGooglePlacesUpsert(camelCaseLocation as Record<string, unknown>);
  }

  // Validate user-created place has required fields
  if (isUserCreated && !bodyValue) {
    // Don't throw - allow empty string
  }

  const googlePlaceId = extractGooglePlaceId(slot, locationDataTyped, finalGooglePlacesData as GooglePlacesDataForPlaceId | null | undefined);

  const base = {
    // Only include _id if it exists (editing existing post location)
    // Backend will generate unique _id for new post locations
    ...(baseId ? { _id: baseId } : {}),
    post_id: (() => {
      if (editingPostId && typeof editingPostId === 'string') return editingPostId;
      const postId = locationDataTyped.post_id;
      if (postId && typeof postId === 'string') return postId;
      return "";
    })(),
    location_id: locationId,
    ...(locationId === '' && googlePlaceId && !isUserCreated ? { google_place_id: googlePlaceId } : {}),
    body: bodyValue, // For user-created: title from slot (required), for Google Places: location name
    // name mirrors body for user-created stops so the backend createItinerary postLocations path
    // (which reads loc.name) also receives the user-entered title
    ...(isUserCreated && bodyValue ? { name: bodyValue } : {}),
    address: addressValue, // For user-created: address from slot (optional), for Google Places: from slot or locationData
    notes, // Per-row notes only — never fall back to locationData (duplicate Google rows)
    itinerary_day_index:
      slot?.dayIndex ??
      locationDataTyped.itinerary_day_index ??
      0,
    itinerary_day_order_index:
      slot?.sequenceIndex ??
      locationDataTyped.itinerary_day_order_index ??
      0,
    image_urls: (() => {
      // Prefer image_urls; fall back to slot.image (timeline often has image but not image_urls)
      const fromUrls =
        Array.isArray(slot?.image_urls) && slot.image_urls.length > 0
          ? slot.image_urls
          : undefined;
      const fromImage =
        typeof slot?.image === 'string' &&
        slot.image.trim().length > 0 &&
        !slot.image.startsWith('data:')
          ? [slot.image.trim()]
          : undefined;
      const slotPrimary = fromUrls ?? fromImage ?? (Array.isArray(slot?.image_urls) ? slot.image_urls : undefined);
      return (
        (slotPrimary !== undefined ? slotPrimary : null) ||
        (locationDataTyped.image_urls &&
        Array.isArray(locationDataTyped.image_urls) &&
        locationDataTyped.image_urls.length > 0
          ? locationDataTyped.image_urls
          : null) ||
        mapPhotosToUrls(locationDataTyped.photos) ||
        []
      );
    })(),
    gt_category: gtCategory,
    slotType: slotType, // Backend derives gt_category from this
    created_at: createdAt,
    updated_at: updatedAt,
    published_at: locationDataTyped.published_at || null,
    is_user_created: isUserCreated,
    location: camelCaseLocation,
    travel_time: slot?.travelTime || locationDataTyped.travel_time || null,
    travel_type: slot?.travelType || locationDataTyped.travel_type || null,
    travel_description:
      slot?.travelDescription ||
      locationDataTyped.travel_description ||
      null,
    timeRange: slot?.timeRange || slot?.time_range || locationDataTyped.timeRange || locationDataTyped.time_range || null,
    budget: slot?.budget !== undefined && slot?.budget !== null ? slot.budget : (locationDataTyped.budget !== undefined && locationDataTyped.budget !== null ? locationDataTyped.budget : null),
  } as unknown as PostLocationPayload;

  const normalizedDayNotes =
    typeof dayNotes === 'string' ? dayNotes.trim() : '';

  if (normalizedDayNotes.length > 0) {
    base.day_notes = normalizedDayNotes;
    base.day_label = normalizedDayNotes;
    base.travelDay = normalizedDayNotes;
  }

  return base;
}

async function buildPostLocationsPayload(
  itineraryData: ItinerarySlot[],
  editingPostId: string | null,
  remixPostObj: Record<string, unknown> | null,
  existingPost?: Post | null // Optional existing post to preserve place photos
): Promise<{ postLocations: PostLocationPayload[]; post_locations: PostLocationPayload[] }> {
  const existing =
    (Array.isArray(remixPostObj?.postLocations)
      ? remixPostObj.postLocations
      : null) ||
    (Array.isArray(remixPostObj?.post_locations)
      ? remixPostObj.post_locations
      : []);

  // Create map of existing postLocations by _id for quick lookup
  const existingPostLocationsMap = new Map<string, Post['postLocations'][0]>();
  if (existingPost?.postLocations) {
    existingPost.postLocations.forEach(pl => {
      if (pl._id) {
        existingPostLocationsMap.set(pl._id, pl);
      }
    });
  }

  // Helper: look up an existing postLocation by slot._id first, then slot.postLocationId.
  // getTimelineId now sets _id = postLocationId, but older state or other callers may
  // still carry a location _id in slot._id while the correct postLocation _id sits in
  // slot.postLocationId.
  const findExistingPostLocation = (slot: Record<string, unknown> | null | undefined): Post['postLocations'][0] | null => {
    if (!slot) return null;
    const byId = typeof slot._id === 'string' ? existingPostLocationsMap.get(slot._id) : undefined;
    if (byId) return byId;
    const byPlId = typeof slot.postLocationId === 'string' ? existingPostLocationsMap.get(slot.postLocationId) : undefined;
    return byPlId ?? null;
  };

  const results: PostLocationPayload[] = [];

  if (Array.isArray(itineraryData) && itineraryData.length > 0) {
    // Process slots in order
    for (const slot of itineraryData) {
      const locationData = slot?.locationData || slot?.location || {};
      let locationDataTyped = locationData as RawLocationData;
      
      // Add the website if already looked up; otherwise it is fetched in the background for
      // a later save — the save itself never waits on it.
      const locationName = (slot?.name || slot?.title || locationDataTyped?.name) as string | undefined;
      const cityAddress = existingPost?.city_identifying_names?.[0] || '';
      locationDataTyped = withKnownWebsite(locationDataTyped, locationName, cityAddress) || locationDataTyped;
      
      // location_id should be the Location _id (MongoDB ObjectId), NOT the google_place_id
      // Only use actual Location _id values for matching existing entries
      const locationIdCandidate = locationDataTyped._id || locationDataTyped.location_id;
      const validLocationId = locationIdCandidate && isMongoObjectIdString(locationIdCandidate)
        ? locationIdCandidate
        : null;

      // Update slot's locationData with fetched website before calling toPostLocation
      const slotWithUpdatedLocationData = {
        ...slot,
        locationData: locationDataTyped,
      };

      const base = toPostLocation(slotWithUpdatedLocationData, editingPostId);

      if (!base) {
        continue;
      }

      // Ensure website is included in location object if it was fetched
      if (locationDataTyped.website && base.location) {
        base.location = {
          ...base.location,
          website: locationDataTyped.website,
        };
      }

      // Handle _id for post locations:
      // For REMIX mode, slots may contain _id from source post's PostLocations
      // We must NEVER preserve these IDs - they cause duplicate key errors in MongoDB
      // Only preserve slot._id in EDIT mode when updating existing PostLocations
      
      // If remixPostObj exists, we're in REMIX mode - always delete _id (IDs belong to source post)
      // Only preserve _id when editing existing post (editingPostId exists) AND not remixing
      if (remixPostObj) {
        // REMIX mode: Always delete _id (it's from source post, not the new remix)
        // Backend will generate new unique _id for each post_location in the remix
        delete base._id;
      } else if (editingPostId) {
        // EDIT mode (not remix): Preserve existing post location _id when updating.
        // Prefer the postLocation _id that maps to existingPostLocationsMap.
        // slot._id may be a location _id if getTimelineId used the old priority order.
        const resolvedPostLocationId =
          (typeof slot?.postLocationId === 'string' && existingPostLocationsMap.has(slot.postLocationId)
            ? slot.postLocationId
            : null) ||
          (typeof slot?._id === 'string' && existingPostLocationsMap.has(slot._id)
            ? slot._id
            : null) ||
          slot?.postLocationId ||
          slot?._id ||
          null;
        if (resolvedPostLocationId) {
          base._id = resolvedPostLocationId;
        } else {
          delete base._id;
        }
      } else {
        // NEW mode or no slot._id: Remove _id, let backend generate unique MongoDB ObjectId
        delete base._id;
      }

      base.post_id = editingPostId || '';
      // location_id should be the Location _id (MongoDB ObjectId), NOT the google_place_id
      // If we don't have a valid Location _id, set to empty string so backend can create/find Location
      // using the location object (google_place_id, name, geoposition, google_places_data)
      base.location_id = validLocationId || '';
      base.itinerary_day_index =
        slot?.dayIndex ??
        base.itinerary_day_index ??
        locationDataTyped.itinerary_day_index ??
        0;
      base.itinerary_day_order_index =
        slot?.sequenceIndex ??
        base.itinerary_day_order_index ??
        locationDataTyped.itinerary_day_order_index ??
        0;
      // Update body: for user-created places use name/body, for Google Places use name/notes/body
      if (slot?.isUserCreated || base.is_user_created) {
        base.body = (slot?.body as string | undefined) || slot?.name || (locationDataTyped.body as string | undefined) || base.body || '';
      } else {
        // Google subtitle is this stop's location name. Notes are a separate field.
        // slot.name is used when the editor renamed this stop; a shifted neighbor title
        // is cleared before this runs.
        base.body = slot?.name || locationDataTyped.name || (slot?.body as string | undefined) || (locationDataTyped.body as string | undefined) || base.body || '';
        
        const googlePlaceIdForUpsert =           extractGooglePlaceId(
          slot,
          locationDataTyped,
            (locationDataTyped.google_places_data || locationDataTyped.googlePlacesData) as GooglePlacesDataForPlaceId | null | undefined
        );
        
        if (!validLocationId && googlePlaceIdForUpsert) {
          base.google_place_id = googlePlaceIdForUpsert;
        }
        
      }
      
      // Update address: for user-created stops, fall back to existingPost address when slot
      // has an empty string (which happens when the DB never stored address and initialization
      // loaded "" from the missing field). For Google Places, keep existing behaviour.
      const isUserCreatedStop = !!(slot?.isUserCreated || base.is_user_created);
      if (isUserCreatedStop && !(slot?.address as string | undefined)) {
        const existingPL = findExistingPostLocation(slot);
        base.address = existingPL?.address || locationDataTyped.address || base.address || '';
      } else {
        base.address = (slot?.address as string | undefined) || locationDataTyped.address || base.address || '';
      }
      
      // Set notes as separate field (backend persists this for place-backed slots)
      base.notes = typeof slot?.notes === 'string' ? slot.notes : '';

      // Set timeRange from slot (backend stores as 'time' in response)
      base.timeRange =
        slot?.timeRange ||
        (slot?.time_range as string | undefined) ||
        base.timeRange ||
        locationDataTyped.timeRange ||
        (locationDataTyped.time_range as string | undefined) ||
        null;

      const normalizedSlotBudget = normalizeCost(
        slot?.budget !== undefined && slot?.budget !== null && slot?.budget !== ''
          ? slot?.budget
          : slot?.cost !== undefined && slot?.cost !== null && slot?.cost !== ''
          ? slot?.cost
          : base.budget !== undefined && base.budget !== null && base.budget !== ''
          ? base.budget
          : locationDataTyped.budget,
        {
          defaultValue: 1000,
          fallbackToDefault: false,
          preserveTextOnNaN: true,
        }
      );

      if (
        !normalizedSlotBudget.isEmpty &&
        normalizedSlotBudget.storageValue !== '' &&
        normalizedSlotBudget.storageValue !== undefined &&
        normalizedSlotBudget.storageValue !== null
      ) {
        base.budget = normalizedSlotBudget.storageValue;
      }

      const mergedDayNotes =
        slot?.dayNotes ||
        slot?.travelDay ||
        base.day_notes ||
        base.day_label ||
        base.travelDay ||
        locationDataTyped.dayNotes ||
        locationDataTyped.travelDay ||
        locationDataTyped.day_notes ||
        locationDataTyped.day_label ||
        null;

      const normalizedMergedDayNotes =
        typeof mergedDayNotes === 'string' ? mergedDayNotes.trim() : '';

      if (normalizedMergedDayNotes.length > 0) {
        base.day_notes = normalizedMergedDayNotes;
        base.day_label = normalizedMergedDayNotes;
        base.travelDay = normalizedMergedDayNotes;
      }

      const existingPLForSlot = findExistingPostLocation(slot);

      // Only set gt_category if explicitly provided as override (backend derives from slotType)
      base.gt_category = (slot?.gt_category as string | undefined) || undefined;

      // Send slotType explicitly (backend derives gt_category from it)
      base.slotType =
        slot?.slotType ||
        locationDataTyped.slotType ||
        base.slotType ||
        undefined;
      
      const locationDataImageUrls = locationDataTyped.image_urls && Array.isArray(locationDataTyped.image_urls) && locationDataTyped.image_urls.length > 0 ? locationDataTyped.image_urls : null;
      const mappedPhotoUrls = mapPhotosToUrls(locationDataTyped.photos);

      // Effective URLs from slot: image_urls OR slot.image (timeline card uses image for display)
      const effectiveSlotImageUrls: string[] | undefined = (() => {
        if (Array.isArray(slot?.image_urls) && slot.image_urls.length > 0) {
          return slot.image_urls;
        }
        if (
          typeof slot?.image === 'string' &&
          slot.image.trim().length > 0 &&
          !slot.image.startsWith('data:')
        ) {
          return [slot.image.trim()];
        }
        if (slot?.image_urls !== undefined && Array.isArray(slot.image_urls)) {
          return slot.image_urls;
        }
        return undefined;
      })();
      
      // Always prioritize slot image URLs from itineraryData state (includes user-saved place photos)
      // slot.image_urls is updated when place photos are saved via PlacePhotoGallery modal
      // We check for both non-empty arrays and empty arrays (to allow clearing photos)
      // Check effective slot URLs FIRST before any fallback to ensure user-saved photos are preserved
      if (effectiveSlotImageUrls !== undefined) {
        // For user-created stops: an empty slot.image_urls means "no photos were added in this
        // session" — it does NOT mean "user cleared all photos". Fall back to existingPost
        // image_urls so that photos saved in a previous session (via PlacePhotoGallery) are
        // not wiped on the next full post save. For Google Places, keep existing behaviour
        // (empty array is a valid "cleared" state because photos come from Google anyway).
        const isUserCreatedStop = !!(slot?.isUserCreated || base.is_user_created);
        if (isUserCreatedStop && effectiveSlotImageUrls.length === 0) {
          const existingPL = findExistingPostLocation(slot);
          base.image_urls = existingPL?.image_urls?.length
            ? existingPL.image_urls
            : [];
        } else {
          base.image_urls = effectiveSlotImageUrls;
        }
      } else {
        // Merge with existing postLocation image_urls if available
        // This preserves photos when state is missing (e.g., after page refresh)
        const existingPL = findExistingPostLocation(slot);
        const existingImageUrls = existingPL?.image_urls;
        
        const finalImageUrls = existingImageUrls || // Priority: existing postLocation
                              locationDataImageUrls ||
                              mappedPhotoUrls ||
                              base.image_urls ||
                              [];
        base.image_urls = finalImageUrls;
      }
      base.created_at =
        base.created_at || locationDataTyped.created_at || new Date().toISOString();
      base.updated_at = new Date().toISOString();
      base.published_at =
        base.published_at !== undefined
          ? base.published_at
          : locationDataTyped.published_at || null;
      base.is_user_created =
        slot?.isUserCreated ??
        locationDataTyped.is_user_created ??
        base.is_user_created ??
        false;

      // For user-created places, ensure Location object is empty and location_id is empty string
      if (slot?.isUserCreated || base.is_user_created) {
        base.location = {
          _id: '',
          name: '',
          google_place_id: undefined,
          google_places_data: undefined,
          geoposition: undefined
        };
        base.location_id = '';
      } else {
        // For Google Places locations, use existing logic
        const existingLocation = (existingPLForSlot?.location ?? {}) as Record<string, unknown>;
        const normalizedLocation = {
          ...deepClone(existingLocation),
          ...deepClone(locationDataTyped),
        };
        
        // Ensure google_place_id is set on location object for backend to create/find Location
        // This is essential when location_id is empty (new Google Place not yet in database)
        if (!normalizedLocation.google_place_id) {
          normalizedLocation.google_place_id = 
            slot?.placeId || 
            locationDataTyped.google_place_id || 
            (normalizedLocation.google_places_data as { id?: string } | undefined)?.id ||
            '';
        }
        
        // Ensure name is set on location object
        if (!normalizedLocation.name) {
          normalizedLocation.name = 
            slot?.name || 
            locationDataTyped.name || 
            (normalizedLocation.google_places_data as { displayName?: { text?: string } } | undefined)?.displayName?.text ||
            '';
        }
        
        // Ensure a3_intl_address is set on location object
        if (!normalizedLocation.a3_intl_address) {
          normalizedLocation.a3_intl_address = 
            (slot?.address as string | undefined) || 
            locationDataTyped.a3_intl_address || 
            (normalizedLocation.google_places_data as { formattedAddress?: string } | undefined)?.formattedAddress ||
            '';
        }
        
        // Ensure geoposition is set for Google Places (backend needs this)
        if (!normalizedLocation.geoposition && slot?.location && typeof slot.location === 'object' && 'lat' in slot.location && 'lng' in slot.location) {
          normalizedLocation.geoposition = {
            type: 'Point' as const,
            coordinates: [slot.location.lng as number, slot.location.lat as number] as [number, number],
          };
        }
        
        // Preserve original google_places_data structure (snake_case) from locationDataTyped
        // The backend expects snake_case fields (place_id, formatted_address, geometry) to create/find Location
        // Only normalize if we don't have the original snake_case structure
        const originalGooglePlacesData = (locationDataTyped.google_places_data ||
          locationDataTyped.googlePlacesData ||
          existingLocation.google_places_data) as Record<string, unknown> | null | undefined;
        const hasSnakeCaseFields = originalGooglePlacesData &&
          (originalGooglePlacesData.place_id ||
            originalGooglePlacesData.formatted_address ||
            originalGooglePlacesData.geometry);
        
        if (hasSnakeCaseFields) {
          // Still normalize so `id` / `formattedAddress` are set for app readers; clone keeps place_id / formatted_address for backend
          const normalizedFromSnake = normalizeGooglePlacesData(
            deepClone(originalGooglePlacesData) as Record<string, unknown>
          );
          normalizedLocation.google_places_data = normalizedFromSnake;
          base.google_places_data = normalizedFromSnake;
        } else {
          // Fallback to normalized version if no snake_case structure available
          normalizedLocation.google_places_data =
            normalizeGooglePlacesData(originalGooglePlacesData) ?? normalizedLocation.google_places_data;
          base.google_places_data =
            normalizeGooglePlacesData(
              ((base.google_places_data as Record<string, unknown> | undefined) ||
                locationDataTyped?.google_places_data ||
                locationDataTyped?.googlePlacesData ||
                existingLocation.google_places_data) as Record<string, unknown> | null | undefined
            ) ?? normalizedLocation.google_places_data;
        }

        base.location = normalizedLocation;

        if (base.budget !== undefined && base.budget !== null) {
          base.location = {
            ...base.location,
            budget: base.budget,
          };
        }

        appendLanguageCodeForGooglePlacesUpsert(base.location as Record<string, unknown>);

        if (!base.google_places_data && normalizedLocation.google_places_data) {
          base.google_places_data = normalizedLocation.google_places_data;
        }

        const baseGpd = base.google_places_data as Record<string, unknown> | undefined;
        if (baseGpd && !Array.isArray(baseGpd.addressComponents)) {
          baseGpd.addressComponents = [];
          base.google_places_data = baseGpd;
        }
      }

      results.push(base);
    }
  }

  const combined =
    results.length > 0
      ? results
      : Array.isArray(existing)
      ? (existing as unknown[]).map((loc: unknown) => {
          if (!loc) {
            return loc as PostLocationPayload;
          }
          const locTyped = loc as PostLocationPayload;
          const clone = deepClone(locTyped) as PostLocationPayload & Record<string, unknown>;
          
          // For remix: Remove PostLocation _id and post_id (these are for the new post)
          // Only preserve location_id (which should be the Location _id, not PostLocation _id)
          delete clone._id;
          if ('post_id' in clone) {
            delete clone.post_id;
          }
          
          // Ensure location_id is set correctly (should be Location _id, not PostLocation _id)
          // If location_id is missing, try to get it from the location object
          if (!clone.location_id && !clone.locationId) {
            const location = clone.location as { _id?: string } | undefined;
            clone.location_id = location?._id || '';
          }
          
          const existingDayNotes =
            clone.day_notes ||
            clone.day_label ||
            clone.travelDay ||
            null;
          const normalizedExistingDayNotes =
            typeof existingDayNotes === 'string' ? existingDayNotes.trim() : '';
          if (normalizedExistingDayNotes.length > 0) {
            clone.day_notes = normalizedExistingDayNotes;
            clone.day_label = normalizedExistingDayNotes;
            clone.travelDay = normalizedExistingDayNotes;
          }

          const cloneRecord = clone as PostLocationPayload & Record<string, unknown>;
          const normalizedGoogleData = normalizeGooglePlacesData(
            (cloneRecord.google_places_data as Record<string, unknown> | undefined) ||
              (cloneRecord.location as Record<string, unknown> | undefined)?.google_places_data as Record<string, unknown> | undefined
          );
          cloneRecord.google_places_data = normalizedGoogleData;
          clone.location = {
            ...deepClone(locTyped.location),
            google_places_data:
              normalizedGoogleData ??
              normalizeGooglePlacesData(locTyped.location?.google_places_data as unknown as Record<string, unknown>),
          };
          if (
            cloneRecord.google_places_data &&
            !Array.isArray((cloneRecord.google_places_data as Record<string, unknown>).addressComponents)
          ) {
            (cloneRecord.google_places_data as Record<string, unknown>).addressComponents = [];
          }
          return clone;
        })
      : [];

  return {
    postLocations: combined as PostLocationPayload[],
    post_locations: combined as PostLocationPayload[],
  };
}

/**
 * Normalize legacy parameters to mode-based parameters
 */
/**
 * Full-replace saves always send itinerary_notes. An editor that has not loaded the
 * stored overview must not replace it with a blank. An empty string is sent only
 * after the user edits the field and clears it.
 */
export function resolveItineraryNotesForSave(
  editorNotes: string | undefined,
  storedNotes: string | undefined,
  editorClearedNotes?: boolean,
): string {
  const edited = typeof editorNotes === 'string' ? editorNotes.trim() : '';
  if (edited) return edited;
  if (editorClearedNotes) return '';
  return typeof storedNotes === 'string' ? storedNotes.trim() : '';
}

function storedItineraryNotes(
  existingPost: Post | null | undefined,
  remixPostObj: Record<string, unknown> | null | undefined,
): string {
  const fromPost = existingPost?.itinerary_notes;
  if (typeof fromPost === 'string' && fromPost.trim()) return fromPost;
  const fromRemix = remixPostObj?.itinerary_notes;
  return typeof fromRemix === 'string' ? fromRemix : '';
}

function readStoredDayNotes(
  existingPost: Post | null | undefined,
  remixPostObj: Record<string, unknown> | null | undefined,
): unknown {
  const rawPost = (existingPost as { raw_itinerary_data?: { day_notes?: unknown } } | null | undefined)
    ?.raw_itinerary_data?.day_notes;
  if (rawPost != null) return rawPost;
  const fromPost = (existingPost?.itinerary_data as { day_notes?: unknown } | undefined)?.day_notes;
  if (fromPost != null) return fromPost;
  const remixData = remixPostObj?.itinerary_data as { day_notes?: unknown } | undefined;
  return remixData?.day_notes;
}

function dayNotesHaveEntries(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value as object).length > 0;
  return false;
}

/**
 * A full-replace save writes itinerary_data.day_notes. An editor that never loaded
 * them must send the stored notes back. An empty object is sent only after the user edits a day note.
 */
export function resolveDayNotesForSave(
  editorNotes: Record<string, string>,
  storedNotes: unknown,
  editorEditedDayNotes?: boolean,
): unknown {
  if (editorEditedDayNotes) return editorNotes;
  if (dayNotesHaveEntries(editorNotes)) return editorNotes;
  if (dayNotesHaveEntries(storedNotes)) return storedNotes;
  return editorNotes;
}

function normalizeParams(params: PostBuilderParams): {
  mode: ItineraryMode;
  modeMetadata: ModeMetadata;
  sourcePostData: Record<string, unknown> | null;
  targetPostId: string | null;
  isDraft: boolean;
  formData: Record<string, unknown>;
  tripTitle: string;
  actualDayCount: number;
  city: CityFormData | null | undefined;
  itineraryData: ItinerarySlot[];
  timelineData?: Array<{ travelDay?: string; [key: string]: unknown }>;
  itineraryNotes?: string;
  itineraryNotesCleared?: boolean;
  dayNotesEdited?: boolean;
  sourceMetadata?: SourceMetadata | null;
  sourceSocialMediaPostId?: string | null;
  existingPost?: Post | null;
  isPrivate?: boolean;
  keepDateStringsWithDayCount?: boolean;
} {
  // Check if using new mode-based params
  if ('mode' in params && 'modeMetadata' in params) {
    const modeParams = params as ModeBasedPostBuilderParams;
    return {
      mode: modeParams.mode,
      modeMetadata: modeParams.modeMetadata,
      sourcePostData: modeParams.modeMetadata.sourcePostData as Record<string, unknown> | null,
      targetPostId: getTargetPostId(modeParams.modeMetadata),
      isDraft: modeParams.isDraft ?? (modeParams.modeMetadata.isDraft || false),
      formData: modeParams.formData,
      tripTitle: modeParams.tripTitle,
      actualDayCount: modeParams.actualDayCount,
      city: modeParams.city,
      itineraryData: modeParams.itineraryData,
      timelineData: modeParams.timelineData,
      itineraryNotes: modeParams.itineraryNotes,
      itineraryNotesCleared: modeParams.itineraryNotesCleared,
      dayNotesEdited: modeParams.dayNotesEdited,
      sourceMetadata: modeParams.sourceMetadata,
      sourceSocialMediaPostId: modeParams.sourceSocialMediaPostId,
      existingPost: (modeParams as { existingPost?: Post | null }).existingPost,
      isPrivate: (modeParams as { isPrivate?: boolean }).isPrivate,
      keepDateStringsWithDayCount: modeParams.keepDateStringsWithDayCount,
    };
  }
  
  // Legacy params - convert to mode-based
  const legacyParams = params as LegacyPostBuilderParams;
  const { isEdit, editingPostId, urlPostId, remixPostObj, isDraft, remixDraftId, existingPost, isPrivate } = legacyParams;
  
  let mode: ItineraryMode;
  let targetPostId: string | null = null;
  let draftFlag = isDraft ?? false;
  
  if (isEdit && editingPostId) {
    mode = ItineraryMode.EDIT;
    targetPostId = editingPostId;
    draftFlag = (remixPostObj?.is_draft as boolean | undefined) ?? false;
  } else if (urlPostId) {
    if (remixDraftId) {
      mode = ItineraryMode.REMIX_DRAFT;
      targetPostId = remixDraftId;
      draftFlag = true;
    } else {
      mode = ItineraryMode.REMIX;
      draftFlag = isDraft ?? false;
    }
  } else {
    mode = ItineraryMode.NEW;
    draftFlag = isDraft ?? false;
  }
  
  // Create mode metadata from legacy params
  const modeMetadata: ModeMetadata = {
    mode,
    sourcePostId: urlPostId || (isEdit ? editingPostId : null),
    sourcePostData: remixPostObj as Record<string, unknown> | null,
    targetPostId,
    isDraft: draftFlag,
  } as ModeMetadata;
  
  return {
    mode,
    modeMetadata,
    sourcePostData: remixPostObj,
    targetPostId,
    isDraft: draftFlag,
    formData: legacyParams.formData,
    tripTitle: legacyParams.tripTitle,
    actualDayCount: legacyParams.actualDayCount,
    city: legacyParams.city,
    itineraryData: legacyParams.itineraryData,
    timelineData: legacyParams.timelineData,
    itineraryNotes: legacyParams.itineraryNotes,
    itineraryNotesCleared: legacyParams.itineraryNotesCleared,
    dayNotesEdited: legacyParams.dayNotesEdited,
    sourceMetadata: legacyParams.sourceMetadata,
    sourceSocialMediaPostId: legacyParams.sourceSocialMediaPostId,
    existingPost,
    isPrivate,
    keepDateStringsWithDayCount: legacyParams.keepDateStringsWithDayCount,
  };
}

export async function buildItineraryPostObject(
  params: PostBuilderParams
): Promise<ItineraryPostObject> {
  // Normalize params to mode-based format
  const normalized = normalizeParams(params);
  const {
    mode,
    modeMetadata,
    sourcePostData: remixPostObj,
    targetPostId: editingPostId,
    isDraft,
    formData,
    tripTitle,
    actualDayCount,
    city,
    itineraryData,
    timelineData,
    itineraryNotes,
    itineraryNotesCleared,
    dayNotesEdited,
    sourceMetadata: passedSourceMetadata,
    sourceSocialMediaPostId: rawSourceSocialMediaPostId,
    existingPost, // Extract existing post
    isPrivate, // Extract isPrivate
    keepDateStringsWithDayCount,
  } = normalized;

  const urlPostId = modeMetadata.sourcePostId;
  const isEdit = mode === ItineraryMode.EDIT;
  const itineraryNotesForSave = resolveItineraryNotesForSave(
    itineraryNotes,
    storedItineraryNotes(existingPost, remixPostObj),
    itineraryNotesCleared,
  );

  const remixSourceMetadata = remixPostObj?.source_metadata as SourceMetadata | undefined;
  const sourceMetadata =
    passedSourceMetadata ??
    (isEdit && remixSourceMetadata ? remixSourceMetadata : null);

  const sourceSocialMediaPostId =
    mongoObjectIdStringOrNull(rawSourceSocialMediaPostId) ??
    (isEdit && remixPostObj
      ? mongoObjectIdStringOrNull(remixPostObj.source_social_media_post_id as string | undefined)
      : null);
  
  // Extract day notes from timelineData (per-day notes)
  // Convert to object format for backend: { "0": "notes", "1": "notes", ... }
  const dayNotesObject: { [key: string]: string } = {};
  if (timelineData && Array.isArray(timelineData)) {
    timelineData.forEach((day, index) => {
      const d = day as { dayNote?: string; travelDay?: string };
      const explicit = typeof d.dayNote === 'string' ? d.dayNote.trim() : '';
      if (explicit) {
        dayNotesObject[String(index)] = explicit;
        return;
      }
      const td = (d.travelDay || '').trim();
      if (!td) return;
      const defaultLabel = `Day ${index + 1}`;
      if (td === defaultLabel || td === 'Arrival' || td === 'Departure') return;
      dayNotesObject[String(index)] = td;
    });
  }
  const dayNotesForSave = resolveDayNotesForSave(
    dayNotesObject,
    readStoredDayNotes(existingPost, remixPostObj),
    dayNotesEdited,
  );

  // Build post body text
  const defaultBody = `${actualDayCount} day itinerary`;
  const postBody: string = tripTitle && tripTitle.trim() ? tripTitle.trim() : defaultBody;

  // Build city metadata using shared utility (mobile app structure)
  // This provides city_identifying_names and user_defined_destinations
  const cityMetadata = buildItineraryCityMetadata(city ?? null);

  // Determine post type (default to 'Itinerary' if not in remixPostObj)
  const postType: string = (remixPostObj?.post_type as string | undefined) || 'Itinerary';

  if (isEdit && editingPostId) {
    // EDIT MODE: Include _id to trigger update
    // Extract _id and other fields from remixPostObj, but ensure editingPostId takes precedence
    const { _id: _remixId, post_type: _remixPostType, ...remixPostObjWithoutId } = remixPostObj || {};
    
    // Build post object with _id FIRST to ensure API recognizes it as an update
    // JavaScript object property order is preserved for string keys in modern engines
    // We must ensure _id is the first property set
    const postObject: Record<string, unknown> = {};
    
    // Set _id FIRST - this is critical for API to recognize as update
    postObject._id = editingPostId;

    // Then add all other fields in order
    postObject.post_type = postType;
    postObject.name = postBody;
    postObject.body = postBody;
    
    // Provide city_identifying_names directly (backend accepts it directly for Itineraries)
    // If a city is provided (even if metadata is empty), use it to allow clearing/updating city
    // Only fall back to remixPostObj if NO city was provided at all
    if (city !== null && city !== undefined) {
      // City was provided - use the metadata (even if empty, this allows clearing the city)
      postObject.city_identifying_names = cityMetadata.city_identifying_names;
    } else {
      // No city provided - fall back to existing city from remixPostObj
      postObject.city_identifying_names = (remixPostObj?.city_identifying_names as string[] | undefined) || [];
    }
    
    // primary_destination: user-chosen destination (city, locality, or country)
    // When set, backend will use it as source of truth and mirror into city_identifying_names
    const primaryDestination = formData.primary_destination as PrimaryDestination | null | undefined;
    if (primaryDestination) {
      postObject.primary_destination = primaryDestination;
    } else if (remixPostObj?.primary_destination) {
      postObject.primary_destination = remixPostObj.primary_destination as PrimaryDestination;
    }

    postObject.is_draft = remixPostObj?.is_draft ?? false;
    // Use isPrivate from params if provided, otherwise fall back to remixPostObj or formData
    const finalIsPrivate = isPrivate !== undefined 
      ? isPrivate 
      : ((formData.is_private as boolean | undefined) ?? 
         (remixPostObj?.is_private as boolean | undefined) ?? 
         false);
    postObject.is_private = finalIsPrivate;
    postObject.is_hidden = false;
    // Do not use `form || remix` for arrays: [] is truthy, so empty wizard defaults would skip remix/post.
    const formTripTypes = (formData.selectedTripTypes as string[] | undefined) ?? [];
    const formTravelWith = (formData.selectedTravelWithTypes as string[] | undefined) ?? [];
    postObject.tag_list =
      formTripTypes.length > 0
        ? formTripTypes
        : (remixPostObj?.tag_list as string[] | undefined) ??
          existingPost?.tag_list ??
          [];
    postObject.travel_with_types =
      formTravelWith.length > 0
        ? formTravelWith
        : (remixPostObj?.travel_with_types as string[] | undefined) ??
          existingPost?.travel_with_types ??
          [];
    
    const remixItineraryData = remixPostObj?.itinerary_data as Record<string, unknown> | undefined;
    
    // Use mobile app structure for user_defined_destinations
    // If a city is provided (even if metadata is empty), use it to allow clearing/updating city
    // Only fall back to remixPostObj if NO city was provided at all
    const userDefinedDestinations = (city !== null && city !== undefined)
      ? cityMetadata.user_defined_destinations  // Use new city metadata (even if empty)
      : (remixItineraryData?.user_defined_destinations as Array<Record<string, unknown>> | undefined) || [];  // Fall back to existing
    
    const itineraryDataToMerge = { ...(remixItineraryData || {}) };
    delete itineraryDataToMerge.user_defined_destinations;
    
    const isUsingDayCount = (formData.is_using_day_count ?? true) as boolean;
    // Handle dates as strings (YYYY-MM-DD) or Date objects - dates are just days, no timezone conversion
    const startDateString = formData.fromDate && !isUsingDayCount
      ? (typeof formData.fromDate === 'string' 
          ? formData.fromDate 
          : formatDateToYYYYMMDD(formData.fromDate as Date))
      : undefined;
    const endDateString = formData.toDate && !isUsingDayCount
      ? (typeof formData.toDate === 'string'
          ? formData.toDate
          : formatDateToYYYYMMDD(formData.toDate as Date))
      : undefined;
    
    // Build itinerary_data with day_notes
    const itineraryDataPayload: Record<string, unknown> = {
      ...itineraryDataToMerge,
      day_count: actualDayCount,
      traveler_count:
        formData.travelerCount ||
        (remixItineraryData?.traveler_count as number | undefined) ||
        1,
      is_using_day_count: isUsingDayCount,
      // null (not omitted) so converting to undated clears the stored dates; saved dates
      // make a trip dated on load.
      start_date_string: startDateString ?? null,
      end_date_string: endDateString ?? null,
      user_defined_destinations: userDefinedDestinations,
    };
    
    itineraryDataPayload.day_notes = dayNotesForSave;

    postObject.itinerary_data = itineraryDataPayload;

    postObject.itinerary_notes = itineraryNotesForSave;

    postObject.collaborators_emails =
      formData.collaborators || remixPostObj?.collaborators_emails || [];
    postObject.vibe_description =
      formData.tripVibe || remixPostObj?.vibe_description || '';
    postObject.budget = formData.selectedBudget || remixPostObj?.budget || null;

    // Add source metadata if provided (from Inspo to Trip flow)
    if (sourceMetadata) {
      postObject.source_metadata = sourceMetadata;
    }
    if (sourceSocialMediaPostId) {
      postObject.source_social_media_post_id = sourceSocialMediaPostId;
    }

    // Finally, spread remixPostObjWithoutId to add any other fields we might have missed
    // But do this AFTER setting all explicit fields to avoid overwriting
    // Extract _id, city-related fields, and is_private from remixPostObjWithoutId to prevent overwriting user's changes
    const { 
      _id: finalCheck, 
      city_identifying_names: _remixCityIdentifyingNames,  // Remove to prevent overwriting
      itinerary_data: _remixItineraryData,  // Remove to prevent overwriting (we set it explicitly above)
      is_private: _remixIsPrivate,  // Remove to prevent overwriting (we set it explicitly above)
      // remixPostObj holds the notes from page load (the stored-notes fallback); spreading
      // them here sent that old text on every save instead of the editor's.
      itinerary_notes: _remixItineraryNotes,
      ...safeRemixData 
    } = remixPostObjWithoutId || {};
    if (finalCheck && __DEV__) {
      warn('[postBuilder] WARNING: _id found in remixPostObjWithoutId after extraction!', {
        finalCheck,
        editingPostId
      });
    }

    Object.assign(postObject, safeRemixData);

    // Re-assert key fields after spreading remix data to preserve user edits
    // This includes city data and cover photo to ensure they're not overwritten by the spread operation
    postObject._id = editingPostId;
    postObject.name = postBody;
    postObject.body = postBody;
    // Re-assert image_urls to ensure cover photo wasn't overwritten by Object.assign
    postObject.image_urls = existingPost?.image_urls || 
                            (remixPostObj?.image_urls as string[] | undefined) || 
                            [];
    // Re-assert is_private to ensure privacy setting wasn't overwritten by Object.assign
    postObject.is_private = finalIsPrivate;
    // Re-assert trip categorization (safeRemixData may overwrite with stale remix snapshot)
    postObject.tag_list =
      formTripTypes.length > 0
        ? formTripTypes
        : (remixPostObj?.tag_list as string[] | undefined) ??
          existingPost?.tag_list ??
          [];
    postObject.travel_with_types =
      formTravelWith.length > 0
        ? formTravelWith
        : (remixPostObj?.travel_with_types as string[] | undefined) ??
          existingPost?.travel_with_types ??
          [];
    // The creator changed the categories in the editor; otherwise the backend keeps auto-filled ones.
    if (formData.categoriesEditedByUser === true) {
      postObject.categories_source = 'user';
    }
    postObject.budget = formData.selectedBudget || remixPostObj?.budget || null;
    // Re-assert vibe_description so Object.assign cannot overwrite a freshly-updated tripVibe
    // with the stale remixPostObj snapshot that was loaded at page open.
    postObject.vibe_description = (formData.tripVibe as string) || (remixPostObj?.vibe_description as string | undefined) || '';
    // Re-assert city data to ensure it wasn't overwritten by Object.assign
    if (city !== null && city !== undefined) {
      postObject.city_identifying_names = cityMetadata.city_identifying_names;
      // Also re-assert itinerary_data.user_defined_destinations
      const currentItineraryData = postObject.itinerary_data as Record<string, unknown> | undefined;
      if (currentItineraryData) {
        currentItineraryData.user_defined_destinations = userDefinedDestinations;
      }
    }

    const { postLocations } = await buildPostLocationsPayload(
      itineraryData,
      editingPostId,
      remixPostObj,
      existingPost // Pass existing post to preserve place photos
    );

    (postObject as Record<string, unknown>).postLocations = postLocations;

    return {
      _id: editingPostId,
      name: postBody,
      body: postBody,
      post_type: postType,
      post: postObject as ItineraryPostObject['post'],
      postLocations: postLocations,
      // Places are the postLocations replace set. Sending the same stops again as
      // itinerary_slots makes the backend store each stop twice.
      itinerary_slots: [],
      lodging_periods: [],
    };
  } else if (urlPostId) {

    // REMIX MODE: Create new post (no _id) or update draft
    // Match working commit f84bb9d64c5a38074a7712fb5b6994d59574a78a
    const remixPost: Record<string, unknown> = {
      post_type: postType,
      ...remixPostObj, // Spread source data directly (as in working commit)
      name: postBody,
      body: postBody,
      // Provide city_identifying_names directly (backend accepts it directly for Itineraries)
      city_identifying_names: cityMetadata.city_identifying_names.length > 0
        ? cityMetadata.city_identifying_names
        : [],
      is_draft: isDraft,
      is_private: isPrivate ?? false,
      is_hidden: false,
    };
    
    // Remove _id from spread - it must not be sent for new remixes
    // (it will be set below ONLY for draft updates)
    // This prevents updating the parent post instead of creating a new remix
    delete remixPost._id;
    
    const remixItineraryDataForRemix = remixPostObj?.itinerary_data as Record<string, unknown> | undefined;
    const isUsingDayCount = (formData.is_using_day_count ?? true) as boolean;
    // Handle dates as strings (YYYY-MM-DD) or Date objects - dates are just days, no timezone conversion
    const startDateString = formData.fromDate && !isUsingDayCount
      ? (typeof formData.fromDate === 'string' 
          ? formData.fromDate 
          : formatDateToYYYYMMDD(formData.fromDate as Date))
      : undefined;
    const endDateString = formData.toDate && !isUsingDayCount
      ? (typeof formData.toDate === 'string'
          ? formData.toDate
          : formatDateToYYYYMMDD(formData.toDate as Date))
      : undefined;
    
    if (cityMetadata.user_defined_destinations.length > 0 || remixItineraryDataForRemix) {
      const remixItineraryDataPayload: Record<string, unknown> = {
        ...(remixItineraryDataForRemix || {}),
        day_count: actualDayCount,
        traveler_count:
          formData.travelerCount ||
          (remixItineraryDataForRemix?.traveler_count as number | undefined) ||
          1,
        is_using_day_count: isUsingDayCount,
        // null (not omitted): the spread above may carry the source trip's dates.
        start_date_string: startDateString ?? null,
        end_date_string: endDateString ?? null,
        user_defined_destinations:
          cityMetadata.user_defined_destinations.length > 0
            ? cityMetadata.user_defined_destinations
            : (remixItineraryDataForRemix?.user_defined_destinations as Array<Record<string, unknown>> | undefined) || [],
      };
      
      remixItineraryDataPayload.day_notes = dayNotesForSave;

      remixPost.itinerary_data = remixItineraryDataPayload;
    }

    remixPost.itinerary_notes = itineraryNotesForSave;
    
    // Only set _id when updating a draft (not for new remixes)
    if (editingPostId && (mode === ItineraryMode.REMIX_DRAFT || isDraft)) {
      remixPost._id = editingPostId;
    }

    // Add source metadata if provided (from Inspo to Trip flow)
    if (sourceMetadata) {
      remixPost.source_metadata = sourceMetadata;
    }
    if (sourceSocialMediaPostId) {
      remixPost.source_social_media_post_id = sourceSocialMediaPostId;
    }

    // Add remix attribution for tracking and compensation
    if (remixPostObj) {
      const parentPostId = remixPostObj._id as string | undefined;
      const parentCreatorId = (remixPostObj.user as { _id?: string } | undefined)?._id as string | undefined;
      const parentCreatorName = (remixPostObj.user as { name?: string } | undefined)?.name as string | undefined;

      if (parentPostId && parentCreatorId) {
        remixPost.remix_parent_post_id = parentPostId;
        remixPost.remix_parent_creator_id = parentCreatorId;
        remixPost.remix_parent_creator_name = parentCreatorName || undefined;
        remixPost.remix_created_at = new Date().toISOString();
        
      } else {
        warn('[postBuilder] REMIX mode - Missing data for remix attribution', {
          hasParentPostId: !!parentPostId,
          hasParentCreatorId: !!parentCreatorId,
          remixPostObjKeys: Object.keys(remixPostObj),
        });
      }
    }

    // Build postLocations for remix mode (needed for slotType persistence)
    const { postLocations } = await buildPostLocationsPayload(
      itineraryData,
      editingPostId || null,
      remixPostObj
    );

    (remixPost as Record<string, unknown>).postLocations = postLocations;

    return {
      _id: editingPostId || undefined,
      name: postBody,
      body: postBody,
      post_type: postType,
      post: remixPost as ItineraryPostObject['post'],
      postLocations: postLocations,
      // Places are the postLocations replace set. Sending the same stops again as
      // itinerary_slots makes the backend store each stop twice.
      itinerary_slots: [],
      lodging_periods: [],
    };
  } else {
    const { postLocations } = await buildPostLocationsPayload(
      itineraryData,
      null,
      remixPostObj,
      existingPost // Pass existing post to preserve place photos (for new posts, this will be null)
    );

    const isUsingDayCount = (formData.is_using_day_count ?? true) as boolean;
    // Handle dates as strings (YYYY-MM-DD) or Date objects - dates are just days, no timezone conversion
    const includeDateStrings = !isUsingDayCount || keepDateStringsWithDayCount === true;
    const startDateString = formData.fromDate && includeDateStrings
      ? (typeof formData.fromDate === 'string' 
          ? formData.fromDate 
          : formatDateToYYYYMMDD(formData.fromDate as Date))
      : undefined;
    const endDateString = formData.toDate && includeDateStrings
      ? (typeof formData.toDate === 'string'
          ? formData.toDate
          : formatDateToYYYYMMDD(formData.toDate as Date))
      : undefined;

    const finalIsPrivate = isPrivate !== undefined 
      ? isPrivate 
      : ((formData.is_private as boolean | undefined) ?? 
         (remixPostObj?.is_private as boolean | undefined) ?? 
         false);

    // Build itinerary_data with day_notes for NEW mode
    const newItineraryDataPayload: Record<string, unknown> = {
      day_count: actualDayCount,
      traveler_count: formData.travelerCount || 1,
      is_using_day_count: isUsingDayCount,
      start_date_string: startDateString,
      end_date_string: endDateString,
      user_defined_destinations: cityMetadata.user_defined_destinations,
    };
    
    newItineraryDataPayload.day_notes = dayNotesForSave;

    const newPost: Record<string, unknown> = {
      name: postBody,
      body: postBody,
      post_type: postType,
      tag_list: formData.selectedTripTypes || [],
      travel_with_types: formData.selectedTravelWithTypes || [],
      city_identifying_names: cityMetadata.city_identifying_names,
      itinerary_data: newItineraryDataPayload,
      is_draft: isDraft, // Fixed: Use isDraft parameter instead of hardcoding false
      is_private: finalIsPrivate,
      is_hidden: false,
      collaborators_emails: formData.collaborators || [],
      vibe_description: formData.tripVibe || '',
      budget: formData.selectedBudget || '',
    };

    newPost.itinerary_notes = itineraryNotesForSave;

    const primaryDestination = formData.primary_destination as PrimaryDestination | null | undefined;
    if (primaryDestination) {
      newPost.primary_destination = primaryDestination;
    }

    // Add source metadata if provided (from Inspo to Trip flow)
    if (sourceMetadata) {
      newPost.source_metadata = sourceMetadata;
    }
    if (sourceSocialMediaPostId) {
      newPost.source_social_media_post_id = sourceSocialMediaPostId;
    }

    (newPost as Record<string, unknown>).postLocations = postLocations;

    const primaryDestinationForTopLevel = formData.primary_destination as
      | PrimaryDestination
      | null
      | undefined;

    return {
      name: postBody,
      body: postBody,
      post_type: postType,
      post: newPost as ItineraryPostObject['post'],
      postLocations: postLocations,
      // Places are the postLocations replace set. Sending the same stops again as
      // itinerary_slots makes the backend store each stop twice.
      itinerary_slots: [],
      lodging_periods: [],
      ...(sourceMetadata ? { source_metadata: sourceMetadata } : {}),
      ...(primaryDestinationForTopLevel
        ? { primary_destination: primaryDestinationForTopLevel }
        : {}),
    };
  }
}

/**
 * buildListPostObject and BuildListPostParams moved to src/utils/listBuilder.ts
 * 
 * Lists now have their own builder: buildListPostPayload from '@/utils/listBuilder'
 */

